import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Button, FormControlLabel, Switch, Typography } from '@mui/material';
import { DeleteForever } from '@mui/icons-material';
import { k8sApi } from '@common-reducers/backend-api/backend-k8s-api';
import { K8sPod, K8sAnalyzerDeletePodsPanelProps } from './k8s-analyzer-types';

/**
 * Determines if a pod is eligible for deletion based on its status
 * and whether it has active sessions.
 */
export const isPodDeletionCandidate = (pod: K8sPod): boolean => {
  return (
    pod.statusString !== 'Running' && 
    (pod.statusString === 'Error' || pod.statusString === 'OOMKilled' || pod.statusString === 'ContainerStatusUnknown') &&
    (!pod.sessions || pod.sessions.length === 0)
  );
};

// Auto delete interval in minutes
const AUTO_DELETE_INTERVAL_MINUTES = 1;
const AUTO_DELETE_INTERVAL_SECONDS = AUTO_DELETE_INTERVAL_MINUTES * 60;

export const K8sAnalyzerDeletePodsPanel: React.FC<K8sAnalyzerDeletePodsPanelProps> = ({
  pods,
  onPodsDeleted,
  setIsConfirmationOpen,
  setPodsToDelete,
  setError,
  onRefresh
}) => {
  const [autoDelete, setAutoDelete] = useState(false);
  const [autoDeleteCountdown, setAutoDeleteCountdown] = useState(AUTO_DELETE_INTERVAL_SECONDS);
  const [isDeleting, setIsDeleting] = useState(false);
  const autoDeleteTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef(AUTO_DELETE_INTERVAL_SECONDS);
  const autoDeleteActiveRef = useRef(false);
  const [deletePod] = k8sApi.useDeletePodByIdActionMutation();
  const nonRunningPodsCount = pods.filter(isPodDeletionCandidate).length;

  const performAutoDeleteNonRunningPods = useCallback(async () => {
    setIsDeleting(true);
    
    try {
      // First refresh the data to ensure we're working with up-to-date pods
      await onRefresh();

      await new Promise(resolve => setTimeout(resolve, 10000));
      
      console.log('Total pods:', pods.length);
      // After refresh, get the non-running pods that have no sessions
      const nonRunningPods = pods.filter(isPodDeletionCandidate);

      console.log('nonRunningPods pods:', nonRunningPods.length);
      
      if (nonRunningPods.length === 0) {
        console.log('No non-running pods to delete');
        setIsDeleting(false);
        return;
      }

      const selectedPods = nonRunningPods.slice(0, 50);
      
      console.log(`Auto-deleting ${selectedPods.length} non-running pods`);
      await Promise.all(selectedPods.map(pod => deletePod(pod.name)));
      onPodsDeleted();
    } catch (err) {
      const _err = err as { data?: { message?: string }; message?: string } | undefined;
      setError(`Failed to auto-delete non-running pods: ${_err?.data?.message || _err?.message || 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
    }
  }, [pods, onRefresh, onPodsDeleted, setError, deletePod]);

  // Setup auto-delete with countdown
  useEffect(() => {
    // Update ref to match state
    autoDeleteActiveRef.current = autoDelete;

    const startAutoDeleteCountdown = () => {
      if (autoDeleteTimerRef.current) {
        clearInterval(autoDeleteTimerRef.current);
      }
      
      // Only reset the countdown when explicitly turning on auto-delete
      if (!autoDeleteActiveRef.current) {
        countdownRef.current = AUTO_DELETE_INTERVAL_SECONDS;
        setAutoDeleteCountdown(AUTO_DELETE_INTERVAL_SECONDS);
      }
      
      autoDeleteTimerRef.current = setInterval(() => {
        countdownRef.current -= 1;
        setAutoDeleteCountdown(countdownRef.current);
        
        if (countdownRef.current <= 0) {
          performAutoDeleteNonRunningPods();
          countdownRef.current = AUTO_DELETE_INTERVAL_SECONDS;
          setAutoDeleteCountdown(AUTO_DELETE_INTERVAL_SECONDS);
        }
      }, 1000);
    };

    if (autoDelete) {
      startAutoDeleteCountdown();
    } else {
      // Reset when turning off
      countdownRef.current = AUTO_DELETE_INTERVAL_SECONDS;
      setAutoDeleteCountdown(AUTO_DELETE_INTERVAL_SECONDS);
    }

    return () => {
      if (autoDeleteTimerRef.current) {
        clearInterval(autoDeleteTimerRef.current);
      }
    };
  }, [autoDelete, performAutoDeleteNonRunningPods]);

  const toggleAutoDelete = () => {
    const newValue = !autoDelete;
    setAutoDelete(newValue);
  };

  const handleDeleteFilteredPods = async () => {
    // First refresh the data
    setIsDeleting(true);
    
    try {
      await onRefresh();
      
      // After refresh, proceed with deletion using fresh data
      const nonRunningPods = pods.filter(isPodDeletionCandidate);
      
      if (nonRunningPods.length === 0) {
        setError('No non-running pods without sessions to delete.');
        setIsDeleting(false);
        return;
      }
      
      const selectedPods = nonRunningPods.slice(0, 50);
      setPodsToDelete(selectedPods);
      setIsConfirmationOpen(true);
    } catch (err) {
      const _err = err as { data?: { message?: string }; message?: string } | undefined;
      setError(`Failed to prepare pods for deletion: ${_err?.data?.message || _err?.message || 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Format time in MM:SS
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {autoDelete && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            border: theme => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            padding: '4px 12px',
            backgroundColor: theme => theme.palette.error.light,
            color: theme => theme.palette.error.contrastText,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme => theme.palette.error.dark,
            }
          }}
          onClick={performAutoDeleteNonRunningPods}
          title="Click to trigger deletion now"
        >
          <DeleteForever fontSize="small" />
          <Typography variant="body2">
            Auto-delete in {formatTime(autoDeleteCountdown)}
          </Typography>
        </Box>
      )}
      
      <FormControlLabel
        control={
          <Switch
            checked={autoDelete}
            onChange={toggleAutoDelete}
            color="error"
          />
        }
        label={`Auto-delete (${AUTO_DELETE_INTERVAL_MINUTES}min)`}
      />
      
      {pods.some(pod => pod.statusString !== 'Running') && (
        <Button
          variant="outlined"
          color="error"
          onClick={handleDeleteFilteredPods}
          disabled={isDeleting}
          sx={{ textTransform: 'none' }}
        >
          {isDeleting ? 'Deleting...' : `Delete Filtered Non-Running Pods (${nonRunningPodsCount})`}
        </Button>
      )}
    </>
  );
};

export default K8sAnalyzerDeletePodsPanel; 